import { isExternalHref, safeHref } from '@hoopoe/shared';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import { helpImageUrl } from '../../lib/help/articles';

/**
 * Renders help Markdown. Raw HTML in the source is dropped (`skipHtml`) and every link and image address goes
 * through `safeHref` (task 43), so articles — including admin-written ones — can't inject markup, scripts or
 * `javascript:` links.
 */
export function Markdown({
  source,
  onInternalLink,
  dir,
}: {
  source: string;
  onInternalLink?: (slug: string, hash?: string) => void;
  /** `auto` for admin-written text, which may be in any language. */
  dir?: 'auto';
}) {
  const components: Components = {
    // Only the generated screenshots (docs/help/images) are shown; any other image stays as its description, so an
    // article can't load pictures from outside Hoopoe.
    img: ({ src, alt }) => {
      const url = typeof src === 'string' ? helpImageUrl(src) : undefined;
      if (!url) return <span className="text-muted-foreground italic">[{alt || 'image'}]</span>;
      return <img src={url} alt={alt ?? ''} loading="lazy" className="my-3 block w-full max-w-3xl rounded-md border shadow-sm" />;
    },
    a: ({ href: raw, children }) => {
      const href = safeHref(raw);
      if (!href) return <span>{children}</span>;
      const help = href.match(/^\/onboarding\/([a-z0-9-]+)(?:#(.+))?$/);
      if (help && onInternalLink)
        return (
          <a
            href={href}
            onClick={e => {
              e.preventDefault();
              onInternalLink(help[1] ?? '', help[2]);
            }}
          >
            {children}
          </a>
        );
      if (!isExternalHref(href)) return <a href={href}>{children}</a>;
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    table: ({ children }) => (
      <div className="relative overflow-x-auto">
        <table>{children}</table>
      </div>
    ),
  };
  return (
    <div className="help-prose" dir={dir}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={components}
        skipHtml
        urlTransform={(url, key) => (key === 'href' ? (safeHref(url) ?? '') : url)}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
